import React from "react";
import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { StatusIcon } from "../ui/StatusIcon";
import type { Widget } from "../../types";
import {
  WidgetKey,
  widgetsCatalog,
  getPlaceholderForSize,
} from "../../data/widgets";
import { useStore } from "../../store";
import { IconButton } from "../ui/IconButton";

interface SideWidgetProps {
  widget: Widget;
  className?: string;
  children?: React.ReactNode;
  size?: "1x1" | "2x1" | "2x2";
}

export const SideWidget: React.FC<SideWidgetProps> = ({
  widget,
  className = "",
  children,
  size = "1x1",
}) => {
  const {
    setWidgetType,
    openWidgetSelector,
    closeWidgetSelector,
    selectingWidgetId,
  } = useStore();

  const hasCurrentWidgetForSize =
    widget.type && widgetsCatalog[widget.type]?.placeholders[size];
  const isSelecting =
    selectingWidgetId === widget.id || !hasCurrentWidgetForSize;
  const openSelector = () => openWidgetSelector(widget.id);
  const closeSelector = () => closeWidgetSelector();
  const selectContent = (type: WidgetKey) => {
    setWidgetType(widget.id, type);
  };

  const getSpecificPlaceholder = (): string | null => {
    return getPlaceholderForSize(widget.type, size);
  };

  const placeholderSrc =
    getSpecificPlaceholder() ||
    (size === "2x2"
      ? "/assets/widget-1.svg"
      : size === "2x1"
      ? "/assets/widget-2-1.svg"
      : "/assets/widget-1-1.svg");
  return (
    <div
      className={`crane-panel min-h-[200px] relative h-full group ${
        isSelecting ? "bg-crane-dark" : ""
      } ${className}`}
    >
      {/* Row-level suggestion overlay is rendered by LayoutRenderer */}
      {!isSelecting && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img
                src={
                  widgetsCatalog[widget.type]?.icon || "/assets/widget-icon.png"
                }
                alt={widgetsCatalog[widget.type]?.label || "widget"}
                className=""
              />
              <button
                className="crane-text-body-lg font-medium text-crane-light-gray hover:text-white"
                onClick={openSelector}
              >
                {widgetsCatalog[widget.type]?.label || "Widget"}
              </button>
            </div>
            <div className="flex space-x-2">
              {widget.alerts > 0 && (
                <StatusIcon
                  icon={
                    <ExclamationCircleIcon className="w-5 h-5 text-crane-accent" />
                  }
                  count={widget.alerts}
                  variant="error"
                />
              )}
              {widget.warnings > 0 && (
                <StatusIcon
                  icon={
                    <ExclamationTriangleIcon className="w-5 h-5 text-crane-amber" />
                  }
                  count={widget.warnings}
                  variant="warning"
                />
              )}
            </div>
          </div>
          <hr className="border-crane-gray-border border-t -mx-2" />
        </>
      )}

      <div className="text-center text-crane-gray h-full -mx-2">
        {isSelecting ? (
          <div className="flex flex-col h-full w-full text-left">
            <div className="flex items-center justify-between pl-2 pb-2 ">
              <div className="crane-text-body-lg">Select content</div>
              {hasCurrentWidgetForSize && (
                <IconButton
                  src="/assets/icon-close.png"
                  alt="Close"
                  onClick={closeSelector}
                />
              )}
            </div>
            <hr className="border-crane-gray-border border-t -mx-2" />
            <div className="overflow-y-auto">
              {Object.values(widgetsCatalog)
                .filter((def) => def.placeholders[size])
                .map((def) => {
                  const isActive = def.key === widget.type;
                  return (
                    <button
                      key={def.key}
                      onClick={() => selectContent(def.key)}
                      className={`w-full text-left px-2 py-3 border-t border-crane-darker flex items-center space-x-3 hover:bg-crane-darker ${
                        isActive ? "bg-crane-darker/60" : ""
                      }`}
                    >
                      <img
                        src={def.icon}
                        alt=""
                        className="w-5 h-5 opacity-80"
                      />
                      <span className="crane-text-body-lg">{def.label}</span>
                    </button>
                  );
                })}
            </div>
          </div>
        ) : (
          children || (
            <div className="flex flex-col items-center justify-start h-full w-full">
              <img
                src={placeholderSrc}
                alt="widget placeholder"
                className="w-full h-full object-contain  object-top"
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};
